import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

/**
 * Fetches company logo from the API.
 * @param companyId - The ID of the company to fetch the logo for.
 * @returns The company logo as a Blob.
 * @throws Error if the API request fails.
 */
export const fetchCompanyLogo = async (companyId: UUID) => {
  if (!companyId) {
    return;
  }

  try {
    // Binary image body (`image/jpeg` or `image/png`), read via `.blob()` —
    // there is no JSON response to validate against a Zod schema here.
    return await vestigasApi.get(ENDPOINT.COMPANY.LOGO.GET(companyId)).blob();
  } catch (error) {
    log.error({
      message: 'Error fetching company logo',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the logo for a company with a given companyId.
 * @param companyId - The ID of the company to fetch the logo for.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryCompanyLogo = <TSelected = string | undefined>(
  companyId: UUID | undefined,
  options: Omit<
    UseQueryOptions<string | undefined, unknown, TSelected>,
    'queryKey' | 'queryFn'
  > = {},
) =>
  useQuery<Blob, unknown, TSelected>({
    enabled: Boolean(companyId),
    gcTime: ms('1h'),
    queryFn: async () => fetchCompanyLogo(companyId),
    queryKey: queryKeysCompany.getLogo(companyId),
    select: (logoData: Blob) =>
      logoData.size > 0 ? URL.createObjectURL(logoData) : undefined,
    staleTime: ms('1h'),
    ...options,
  });
